import {
  Alert,
  DatePicker,
  DatePickerProps,
  Divider,
  Flex,
  Form,
  TimePicker,
} from "antd";
import type { FormInstance } from "antd";
import { modalEyebrowStyle } from "components/modals/ModalChrome";
import { Dayjs } from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useBusinessSettings } from "hooks/queries/business-settings";
import { useScheduleStateOptional } from "pages/schedule/filter/ScheduleFilterContext";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { ClassType } from "shared/types";
import {
  combineDateTime,
  DEFAULT_DATE_FORMAT,
  DEFAULT_DATE_SUBMIT_FORMAT,
  DEFAULT_TIMEZONE,
  now,
  tzDate,
} from "shared/utils/dayjs";
import { buildRRule, RecurrenceEnd, RecurrenceType } from "shared/utils/rrule";
import { AutoCloseTimePicker } from "./AutoCloseTimePicker";
import { buildAvailabilityDisabledTime } from "./buildAvailabilityDisabledTime";
import { buildNoValidTimeMessage } from "./buildNoValidTimeMessage";
import { RecurrenceSelect } from "./RecurrenceSelect";
import { useDisabledStartTime } from "./useDisabledStartTime";
import { useInstructorAvailability } from "./useInstructorAvailability";
import { SectionEyebrow } from "./SectionEyebrow";

const TIME_FORMAT = "HH:mm";
const TIME_MINUTE_STEP = 5;
const FIELD_STYLE = { flex: 1, marginBottom: 12 };
const FULL_WIDTH = { width: "100%" };

interface TimeSectionProps {
  form: FormInstance;
  selectedClass: ClassType | undefined;
  scheduleDate: Dayjs | undefined;
  startTime: Dayjs | undefined;
  timeEnabled: boolean;
  maxOccurrences: number | undefined;
  maxScheduleDate: Date | undefined;
  // Ceiling for the START date when it differs from the series ceiling (an
  // unactivated first-use package: start within the auto-start ceiling,
  // series within start + validity). Falls back to maxScheduleDate.
  maxStartDate?: Date;
  // Earliest date the selected packages can pay for (the latest purchase
  // date across them) — the lower bound pairing with maxScheduleDate's
  // expiry upper bound. Only bites once past dates are selectable, or on a
  // first-use package where an earlier first class would end it before a
  // class already booked on it (then minStartFromBookedClass is true and the
  // message says so).
  minScheduleDate?: Date;
  minStartFromBookedClass?: boolean;
  // Lets the picker accept dates before today. Only the create panel passes
  // it: admins may record a class that already happened (single occurrence,
  // see RecurrenceSelect). The reschedule modal (UpdateScheduleFormBody.tsx)
  // keeps the default and still refuses the past, matching the calendar's
  // drag-reschedule guard.
  allowPastDates?: boolean;
  showRecurrence?: boolean;
  // When set, render as a numbered create-panel section (eyebrow + locked dim)
  // instead of the default titled Card used by the update/reschedule modal.
  sectionNumber?: number;
  // Resolved plain-string instructor id (never the labelInValue {value,label}
  // shape) — only ever passed by CreateScheduleDrawer.tsx. Left undefined
  // (the default), the whole instructor-availability apparatus below stays
  // fully inert, which is what keeps UpdateScheduleFormBody.tsx (reschedule)
  // out of scope without any gating on its end.
  instructorId?: string;
  // Fires whenever "is timing currently blocked by instructor availability"
  // changes, folding in whichever time-entry mode is active (the shared
  // field vs. per-day times) — see the effect below. Only ever passed by
  // CreateScheduleDrawer.tsx, which uses it to gate Jadwalkan.
  onAvailabilityBlockChange?: (blocked: boolean) => void;
}

export const TimeSection = ({
  form,
  selectedClass,
  scheduleDate,
  startTime,
  timeEnabled,
  maxOccurrences,
  maxScheduleDate,
  maxStartDate,
  minScheduleDate,
  minStartFromBookedClass = false,
  allowPastDates = false,
  showRecurrence = true,
  sectionNumber,
  instructorId,
  onAvailabilityBlockChange,
}: TimeSectionProps) => {
  // Whether any individual day is fully blocked while per-day times are
  // active — reported by RecurrenceSelect, since it owns each day's own
  // availability grid (see buildAvailabilityDisabledTimeForWeekday).
  const [perDayBlocked, setPerDayBlocked] = useState(false);
  // Only past times are disabled now; clashing times stay selectable.
  const startTimeDisabled = useDisabledStartTime({
    scheduleDate,
    allowPastTimes: allowPastDates,
  });

  // Drives hiding the top-level Waktu Mulai/Waktu Selesai row below once
  // RecurrenceSelect's per-day pickers have taken over — watched early since
  // the availability apparatus below is also suppressed while per-day times
  // are active (a single uniform disabled-time grid doesn't apply once each
  // day gets its own independently-picked time).
  const perDayTimesActive = !!Form.useWatch("per_day_times", form);

  const recurrenceType =
    (Form.useWatch("recurrence_type", form) as RecurrenceType | undefined) ??
    "none";
  const recurrenceDays = Form.useWatch<string[] | undefined>(
    "recurrence_days",
    form,
  );
  const endConditionType = Form.useWatch("end_condition_type", form) as
    | "until"
    | "count"
    | undefined;
  const endConditionUntil = Form.useWatch("end_condition_until", form) as
    | Dayjs
    | undefined;
  const endConditionCount = Form.useWatch("end_condition_count", form) as
    | number
    | undefined;

  // "Enough input to build a valid, bounded probe" — mirrors exactly what
  // checkIfValidRRule/buildRRule themselves require for a real submission
  // (not a hand-picked subset): a concrete end condition once a recurrence
  // type is chosen, and non-empty recurrence_days specifically for weekly.
  const sufficientRecurrenceInput =
    recurrenceType === "none" ||
    (!!endConditionType &&
      (endConditionType === "until"
        ? !!endConditionUntil
        : !!endConditionCount) &&
      (recurrenceType !== "weekly" || !!recurrenceDays?.length));

  const sufficientInput =
    !!instructorId &&
    !!selectedClass &&
    !!scheduleDate &&
    sufficientRecurrenceInput;

  // Same rrule string a real submission would build (buildRRule), reused
  // as-is rather than reinvented for the preview.
  const rrule = useMemo(() => {
    if (recurrenceType === "none" || !sufficientRecurrenceInput) {
      return undefined;
    }
    const end: RecurrenceEnd =
      endConditionType === "count"
        ? { type: "count", count: endConditionCount as number }
        : { type: "until", date: (endConditionUntil as Dayjs).toISOString() };
    return buildRRule(
      recurrenceType,
      end,
      recurrenceType === "weekly" ? recurrenceDays : undefined,
      DEFAULT_TIMEZONE,
    );
  }, [
    recurrenceType,
    sufficientRecurrenceInput,
    endConditionType,
    endConditionCount,
    endConditionUntil,
    recurrenceDays,
  ]);

  // Format scheduleDate directly, NOT through tzDate(): scheduleDate is a
  // date-only DatePicker value with no meaningful "instant" — its wall-clock
  // numbers already ARE the calendar date the admin picked. Routing it
  // through tzDate() first converts it to a native Date (fixing an instant
  // in the ADMIN'S browser-local zone) and then re-projects that instant
  // into Asia/Jakarta, which silently shifts the date backward a day for any
  // admin whose system clock is east of Jakarta (Bali/WITA +8, Papua/WIT +9,
  // Australia, etc. — a real userbase for an Indonesia-wide studio SaaS).
  // combineDateTime (shared/utils/dayjs.ts) already formats a picked date
  // this same direct way for exactly this reason; this now matches it.
  const startDate = scheduleDate
    ? scheduleDate.format(DEFAULT_DATE_SUBMIT_FORMAT)
    : undefined;

  const { data: businessSettings } = useBusinessSettings();
  // Mirrors the backend's own employee_management_enabled && sub_flag
  // structure exactly, not just "any of the three sub-flags on their own" —
  // if this is false the endpoint would only ever answer fully-unrestricted,
  // so skip the round trip entirely.
  const anyRuleActive =
    !!businessSettings &&
    ((businessSettings.employee_management_enabled &&
      (businessSettings.employee_management_working_hours_enabled ||
        businessSettings.employee_management_leave_enabled)) ||
      businessSettings.schedule_conflict_prevention_enabled);

  const { data: availability } = useInstructorAvailability({
    instructorId,
    classId: selectedClass?.id,
    startDate,
    rrule,
    timezone: DEFAULT_TIMEZONE,
    sufficientInput,
    anyRuleActive,
  });

  const { disabledTime: availabilityDisabledTime, impossibleDates } =
    useMemo(
      () => buildAvailabilityDisabledTime(availability, selectedClass?.duration ?? 0),
      [availability, selectedClass?.duration],
    );

  // Union of past-time disabling (today only) and availability-based
  // disabling — past times stay disabled regardless of availability data.
  const mergedStartTimeDisabled = useMemo(
    () => () => {
      const past = startTimeDisabled();
      const avail = availabilityDisabledTime();
      const hours = new Set([...past.disabledHours(), ...avail.disabledHours()]);
      return {
        disabledHours: () => Array.from(hours),
        disabledMinutes: (hour: number) =>
          Array.from(
            new Set([...past.disabledMinutes(hour), ...avail.disabledMinutes(hour)]),
          ),
      };
    },
    [startTimeDisabled, availabilityDisabledTime],
  );

  // Availability rules alone (not merged with past-time disabling) blocking
  // every hour of the day — computed separately from the merged picker so a
  // today-and-it's-late situation never gets misreported as "instructor
  // unavailable."
  const noValidTime =
    sufficientInput && anyRuleActive && !!availability &&
    availabilityDisabledTime().disabledHours().length >= 24;

  const noValidTimeMessage = useMemo(
    () => buildNoValidTimeMessage(impossibleDates, recurrenceType !== "none"),
    [impossibleDates, recurrenceType],
  );

  // Stale-selection guard: if instructor/class/recurrence change such that
  // the currently-picked start_time is no longer valid, clear it rather than
  // leave a stale, now-wrong value sitting in the field until submit. Keyed
  // only on the availability result changing (via form.getFieldValue, not
  // the watched `startTime` prop) — depending on the field's own value here
  // would risk the effect re-triggering itself.
  //
  // Exception: when NO time at all works this day (noValidTime), the value
  // is deliberately left in place instead of cleared — clearing it would
  // also empty the calendar's ghost-preview box (which requires a start_time
  // to render), making the admin's in-progress draft silently vanish. The
  // field's own `disabled` state (below) covers blocking submission instead.
  //
  // Also skipped entirely while per-day times are active: the field is
  // hidden then, and only ever read (not written) by RecurrenceSelect to
  // seed each day's own picker — the comment above the hidden Form.Item
  // promises its value stays untouched while hidden. Without this guard, an
  // aggregate availability change (e.g. switching instructor) could silently
  // clear it in the background, and a day later toggled off/on would then
  // re-seed from `undefined` instead of the time it actually held.
  useEffect(() => {
    if (perDayTimesActive) return;
    const current = form.getFieldValue("start_time") as Dayjs | undefined;
    if (!current || noValidTime) return;
    const check = availabilityDisabledTime();
    const hour = current.hour();
    const minute = current.minute();
    const isNowInvalid =
      check.disabledHours().includes(hour) ||
      check.disabledMinutes(hour).includes(minute);
    if (isNowInvalid) {
      form.setFieldValue("start_time", undefined);
    }
  }, [availabilityDisabledTime, noValidTime, form, perDayTimesActive]);

  // Reports whether timing is currently blocked by instructor availability,
  // regardless of which time-entry mode is active — the shared field's
  // noValidTime, or (once per-day times take over) that mode's own blocked
  // state, forwarded up unchanged. CreateScheduleDrawer.tsx uses this single
  // signal to gate Jadwalkan without needing to know which mode is active.
  useEffect(() => {
    onAvailabilityBlockChange?.(perDayTimesActive ? perDayBlocked : noValidTime);
  }, [onAvailabilityBlockChange, perDayTimesActive, perDayBlocked, noValidTime]);

  // Jumps the Jadwal calendar to whichever week "Tanggal Mulai" lands in, so
  // its ghost preview is actually visible without the admin manually
  // navigating there. Optional (not useScheduleState()) because this
  // component is also reused by UpdateScheduleFormBody.tsx, which isn't
  // guaranteed to render under ScheduleProvider — same defensive pattern as
  // ActionedSchedules.tsx. Deliberately wired into the DatePicker's own
  // onChange below (fires only on a genuine click in the picker) rather than
  // a useEffect on the watched value — a plain effect would also fire when
  // the drawer opens with its default schedule_date (today, from the
  // Form.Item's initialValue) via the generic Add button or nav, silently
  // snapping the calendar back to today's week even if the admin had
  // deliberately navigated to a future one before clicking Add.
  const setView = useScheduleStateOptional()?.setView;

  // The package bounds arrive only after a package is picked, so a date/count
  // entered first can silently become invalid. Re-validate those fields the
  // moment the bounds change so the violation is flagged inline, in either
  // order of entry — not just surfaced as a disabled submit button.
  const expiryTime = maxScheduleDate?.getTime();
  const purchaseTime = minScheduleDate?.getTime();
  useEffect(() => {
    const toRevalidate = ["schedule_date", "end_condition_count"].filter(
      (name) => form.getFieldValue(name) !== undefined
    );
    if (toRevalidate.length) {
      form.validateFields(toRevalidate).catch(() => {});
    }
  }, [expiryTime, purchaseTime, maxOccurrences, form]);

  const requiredSelectRule = (label: string) => [
    {
      required: true,
      message: `${translate("form.error.select")} ${label}`,
    },
  ];

  const endTimeValue =
    startTime && selectedClass && scheduleDate
      ? combineDateTime(scheduleDate, startTime)?.add(
          selectedClass.duration,
          "minute"
        )
      : undefined;

  // Historical dates stay out unless the caller opted in (allowPastDates —
  // the create panel's backdated schedules); intra-day time filtering lives
  // in useDisabledStartTime so the picker rules stay in one place. Dates
  // outside the selected packages' window (before the latest purchase, past
  // the earliest expiry) are blocked too, so the admin sees the package
  // boundary in the picker rather than only at save.
  const disabledDates: DatePickerProps["disabledDate"] = (current) => {
    if (!current) return false;
    // current is an AntD-sourced calendar cell (no utc/timezone plugins — its
    // very type lacks .tz/.local); reduce to a native Date and rebuild on our
    // instance so we never mix instances in a single comparison chain.
    const day = tzDate(current.toDate());
    if (!allowPastDates && day.valueOf() < now().startOf("day").valueOf()) {
      return true;
    }
    if (
      !!minScheduleDate &&
      day.valueOf() < tzDate(minScheduleDate).startOf("day").valueOf()
    ) {
      return true;
    }
    const startBound = maxStartDate ?? maxScheduleDate;
    return (
      !!startBound &&
      day.valueOf() > tzDate(startBound).endOf("day").valueOf()
    );
  };

  const body = (
    <>
      <Flex gap={12}>
        <Form.Item
          name="schedule_date"
          label={translate("schedule.drawer.start_date")}
          rules={[
            ...requiredSelectRule(translate("label.schedule_date")),
            {
              // Flags a date that fell out of range after the package was
              // picked (the picker blocks fresh out-of-range picks).
              validator: (_, value?: Dayjs) => {
                if (!value) return Promise.resolve();
                const startBound = maxStartDate ?? maxScheduleDate;
                if (
                  startBound &&
                  tzDate(value).isAfter(tzDate(startBound).endOf("day"))
                ) {
                  return Promise.reject(
                    new Error(
                      translate("schedule.drawer.date_past_package_expiry")
                    )
                  );
                }
                if (
                  minScheduleDate &&
                  tzDate(value).isBefore(tzDate(minScheduleDate).startOf("day"))
                ) {
                  return Promise.reject(
                    new Error(
                      translate(
                        minStartFromBookedClass
                          ? "schedule.drawer.date_before_package_window"
                          : "schedule.drawer.date_before_package_purchase"
                      )
                    )
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
          initialValue={now()}
          style={FIELD_STYLE}
        >
          <DatePicker
            disabled={!timeEnabled}
            style={FULL_WIDTH}
            format={DEFAULT_DATE_FORMAT}
            disabledDate={disabledDates}
            onChange={(date) => {
              form.setFieldValue("start_time", undefined);
              if (date) {
                setView?.((prev) => ({ ...prev, date: date.format("YYYY-MM-DD") }));
              }
            }}
          />
        </Form.Item>
      </Flex>
      {/* Hidden once per-day times are active (RecurrenceSelect's per-day
          pickers below have taken over) — editing this then does nothing,
          which read as confusing/disconnected. start_time's Form.Item keeps
          the library default preserve:true (NOT preserve={false}) so hiding
          it doesn't clear its value: RecurrenceSelect.tsx still reads it to
          seed each day's initial time, and the per-day submit path in
          CreateScheduleDrawer.tsx still falls back to it. */}
      {!perDayTimesActive && (
        <>
          <Flex gap={12}>
            <Form.Item
              name="start_time"
              label={translate("schedule.drawer.start_time")}
              rules={requiredSelectRule(translate("label.start_end_time"))}
              style={FIELD_STYLE}
            >
              <AutoCloseTimePicker
                disabled={!timeEnabled || noValidTime}
                style={FULL_WIDTH}
                format={TIME_FORMAT}
                minuteStep={TIME_MINUTE_STEP}
                disabledTime={mergedStartTimeDisabled}
                placeholder={`${translate("form.placeholders.select")} ${translate(
                  "label.start_time"
                )}`}
              />
            </Form.Item>

            <Form.Item
              label={translate("schedule.drawer.end_time")}
              style={FIELD_STYLE}
            >
              <TimePicker
                style={FULL_WIDTH}
                format={TIME_FORMAT}
                value={endTimeValue}
                disabled
                allowClear={false}
                suffixIcon={null}
                placeholder="--:--"
              />
            </Form.Item>
          </Flex>
          {noValidTime && (
            <Alert
              type="warning"
              showIcon
              message={noValidTimeMessage}
              style={{ marginBottom: 12 }}
            />
          )}
        </>
      )}
      {timeEnabled && showRecurrence && (
        <>
          <Divider style={{ margin: "4px 0 12px" }} />
          <RecurrenceSelect
            selectedDate={scheduleDate}
            maxOccurrences={maxOccurrences}
            maxScheduleDate={maxScheduleDate}
            availability={availability}
            duration={selectedClass?.duration}
            onPerDayBlockChange={setPerDayBlocked}
          />
        </>
      )}
    </>
  );

  // Create panel: numbered eyebrow section, dimmed while locked (still gated by
  // the disabled fields). Update/reschedule modal: the original titled Card.
  if (sectionNumber !== undefined) {
    return (
      <div style={{ marginBottom: 22, opacity: timeEnabled ? 1 : 0.5 }}>
        <SectionEyebrow
          num={sectionNumber}
          label={translate("schedule.drawer.time_recurrence")}
        />
        {body}
      </div>
    );
  }

  // Update/reschedule modal: the prototype's bordered field section with an
  // uppercase eyebrow label instead of a titled Card.
  return (
    <div
      style={{
        border: `1px solid ${colors.line}`,
        borderRadius: 12,
        padding: 16,
        marginTop: 8,
      }}
    >
      <span style={{ ...modalEyebrowStyle, marginBottom: 12 }}>
        {translate("schedule.drawer.time_recurrence")}
      </span>
      {body}
    </div>
  );
};
